/**
 * Design presets — the effective rendered identity of every selectable
 * design in the studio.
 *
 * Why this file exists (2026-11-03 design audit): applyBankTemplate used
 * to set a per-bank texture and then a final catch-all else-branch
 * overwrote it with guilloche_bg for almost every template, and its font
 * mapping only honoured Courier/Helvetica/Arial spec strings. The result
 * was that 43 of the 48 bank templates rendered with one of only two
 * (texture, font, paper) combinations — designs differed by colour and
 * printed name only. The check-style buttons likewise changed nothing
 * but the style id, so Personal / Business / Counter / Money Order
 * rendered the identical check.
 *
 * These maps are the single authoritative source: one entry per design,
 * built ONLY from real studio parameters (texture / font / paper ids
 * from CheckBuilder's TEXTURES, FONTS and PAPER_COLORS lists, border
 * styles from BorderlineEngine). A unit test
 * (designPresets.test.ts) enforces coverage, validity, and that no two
 * designs collapse to the same effective combination.
 */

export interface TemplateDesign {
    texture: string;
    font: string;
    paper: string;
    /** Texture opacity override (treasury / ultra / karen keep theirs). */
    opacity?: number;
}

/** Effective design for every bank template id and premium template id. */
export const TEMPLATE_DESIGN: Record<string, TemplateDesign> = {
    // ---- Major banks -------------------------------------------------
    // MAJOR rebuild wave (2026-11-05): the evidenced catalogue ground
    // for the HC banks is the safety treatment ('security', the
    // studio's woven safety screen — HC's ground is literally named
    // Security Weave) on white safety paper; the decorative textures
    // the old builds carried live on the _STUDIO variants below.
    // BOFA's marble was Deluxe premium/generic stock, not the HC
    // ground. BOFA_CLASSIC carried the marble on the owner's
    // specimen until the OWNER RULING of 2026-11-05 ("use there
    // bank original template designs") rebuilt it to this same
    // evidenced design (no distinct BofA classic design exists in
    // the evidence — the CHASE_PRIVATE precedent); the specimen
    // marble build is preserved as BOFA_CLASSIC_STUDIO below.
    BOFA: { texture: 'security', font: 'helvetica', paper: 'white' },
    BOFA_CLASSIC: { texture: 'security', font: 'helvetica', paper: 'white' },
    BOFA_BUSINESS_V2: { texture: 'blue_wave', font: 'arial', paper: 'white' },
    US_TREASURY_OFFICIAL: { texture: 'fiber_optic', font: 'courier', paper: 'treasury_green' },
    CHASE: { texture: 'security', font: 'helvetica', paper: 'white' },
    // CHASE_PRIVATE (MAJOR rebuild wave, 2026-11-05): no distinct
    // Private Client check design exists in the evidence (programme
    // research graded it (c) NOT FOUND), so the template carries the
    // one evidenced Chase catalogue design — the same effective
    // design as CHASE (Deluxe Standard). The pre-rebuild prestige
    // build (garamond on cream, gold) is preserved verbatim as
    // CHASE_PRIVATE_STUDIO below.
    CHASE_PRIVATE: { texture: 'security', font: 'helvetica', paper: 'white' },
    WELLS: { texture: 'security', font: 'helvetica', paper: 'white' },
    CITI: { texture: 'security', font: 'helvetica', paper: 'white' },
    USAA: { texture: 'linen', font: 'helvetica', paper: 'white' },
    // GreenFi EXACT rebuild (2026-11-04): the specimen paper is
    // SMOOTH and near-white — 'plain' texture on the palest green
    // paper. The old entry's linen weave (a visible grid + fibres
    // over mint) was the rejected first build ("too greenish...
    // a little bit white background with a touch of light green").
    GREENFI: { texture: 'plain', font: 'arial', paper: 'light_green' },
    // The rejected first GreenFi build, preserved as its own
    // library entry per Princewill's disposition (2026-11-04):
    // the name "GreenFi" belongs to the exact rebuild, so the
    // mint/linen build lives on under this id with its original
    // design tokens (linen weave, mint identity colours, strong
    // frame colour in bankTemplates/design maps).
    GREENFI_MINT: { texture: 'linen', font: 'arial', paper: 'light_green' },
    // ANB EXACT rebuild (2026-11-04): the specimen face is the
    // Deluxe scenic — sky + clouds over a golden wheat field,
    // drawn whole by the scenic_field background (the field's
    // grain is the yellow pattern). The old entry's cloud_band
    // painted only the blue sky, mid-face, and the yellow field
    // never existed — the owner's named defect.
    ANB: { texture: 'scenic_field', font: 'arial', paper: 'cream' },
    // The rejected first ANB build, preserved as its own library
    // entry (the GREENFI_MINT pattern, owner's disposition): the
    // name "ANB Bank" belongs to the exact rebuild, so the
    // cloud-band build lives on under this id with its original
    // design tokens (cloud_band over cream, DOUBLE border, navy
    // identity colours in bankTemplates/design maps).
    ANB_SKY: { texture: 'cloud_band', font: 'arial', paper: 'cream' },
    PNC: { texture: 'blue_wave', font: 'arial', paper: 'light_blue' },
    TD: { texture: 'cotton', font: 'arial', paper: 'light_green' },
    // CAPITAL_ONE / US_BANK (MAJOR rebuild wave, 2026-11-05): the
    // grounds become the evidenced safety treatment — 'prismatic'
    // is an HC High-Security-only pattern name and 'laser_security'
    // a decorative texture; neither is the bank's standard-tier
    // catalogue ground. The old builds are preserved verbatim as
    // _STUDIO variants below.
    CAPITAL_ONE: { texture: 'security', font: 'helvetica', paper: 'white' },
    US_BANK: { texture: 'security', font: 'helvetica', paper: 'white' },
    // MAJOR wave Studio Variants (2026-11-05): the nine majors'
    // pre-rebuild design tokens, preserved verbatim (seven banks;
    // CHASE and TD changed no effective-design field in the rebuild,
    // so a variant would duplicate the main — the BMO precedent).
    BOFA_STUDIO: { texture: 'cloud_marble', font: 'helvetica', paper: 'white' },
    // BOFA_CLASSIC rebuild (owner ruling, 2026-11-05): the
    // specimen marble build's design tokens, preserved verbatim.
    BOFA_CLASSIC_STUDIO: { texture: 'cloud_marble', font: 'courier', paper: 'light_blue' },
    CHASE_PRIVATE_STUDIO: { texture: 'security', font: 'garamond', paper: 'cream' },
    WELLS_STUDIO: { texture: 'linen', font: 'helvetica', paper: 'white' },
    CITI_STUDIO: { texture: 'geometric_diamond', font: 'helvetica', paper: 'white' },
    PNC_STUDIO: { texture: 'blue_wave', font: 'arial', paper: 'light_blue' },
    CAPITAL_ONE_STUDIO: { texture: 'prismatic', font: 'helvetica', paper: 'white' },
    US_BANK_STUDIO: { texture: 'laser_security', font: 'helvetica', paper: 'white' },
    HUNTINGTON: { texture: 'security', font: 'georgia', paper: 'white' },
    FIFTH_THIRD: { texture: 'security', font: 'arial', paper: 'white' },
    KEYBANK: { texture: 'security', font: 'helvetica', paper: 'white' },
    // REGIONAL wave-1 Studio Variants (2026-11-05): the banks'
    // pre-rebuild design tokens, preserved verbatim (five banks;
    // BMO's rebuild changed no design token — see its entry).
    REGIONS_STUDIO: { texture: 'royal_damask', font: 'garamond', paper: 'white' },
    TRUIST_STUDIO: { texture: 'interference', font: 'helvetica', paper: 'white' },
    HUNTINGTON_STUDIO: { texture: 'parchment', font: 'georgia', paper: 'white' },
    FIFTH_THIRD_STUDIO: { texture: 'multi_tone', font: 'arial', paper: 'white' },
    KEYBANK_STUDIO: { texture: 'circuit_board', font: 'helvetica', paper: 'white' },
    // REGIONAL wave-2 Studio Variants (2026-11-05): the banks'
    // pre-rebuild design tokens, preserved verbatim.
    CITIZENS_STUDIO: { texture: 'micro_embossed', font: 'arial', paper: 'light_green' },
    ZIONS_STUDIO: { texture: 'rough', font: 'helvetica', paper: 'white' },
    COMERICA_STUDIO: { texture: 'toner_adhesion', font: 'arial', paper: 'white' },
    FIRST_HORIZON_STUDIO: { texture: 'fiber_optic', font: 'georgia', paper: 'white' },
    M_AND_T_STUDIO: { texture: 'particles', font: 'times', paper: 'white' },
    // REGIONAL wave-2 rebuild (2026-11-05): CITIZENS' evidenced
    // catalogue ground is the safety treatment on white safety
    // paper — its old green tint was no named safety design (c).
    CITIZENS: { texture: 'security', font: 'arial', paper: 'white' },
    // ---- Digital banks -------------------------------------------------
    // DIGITAL wave rebuilds (2026-11-05): ALLY and DISCOVER take the
    //   evidenced catalogue ground — the safety treatment on white
    //   safety paper — under the programme-default Deluxe Standard
    //   set (their printers are NOT FOUND (c); the default is
    //   flagged in the ledger, never dressed as evidence). ALLY's
    //   plum primary weaves as its intended safety tint (the
    //   Truist/CU pattern); DISCOVER's orange is its MARK's ink, not
    //   its paper — the weave takes the neutral override
    //   (TEMPLATE_WEAVE_INK) and the name ink splits to near-black
    //   (TEMPLATE_WORDMARK_INK), the published letters being black
    //   with only the "O" orange (b). The decorative pre-rebuild
    //   grounds live on the _STUDIO variants. ECHECK's ground is
    //   decided with its own wave below (Specialty).
    ALLY: { texture: 'security', font: 'helvetica', paper: 'white' },
    ALLY_STUDIO: { texture: 'digital_hive', font: 'helvetica', paper: 'white' },
    DISCOVER: { texture: 'security', font: 'arial', paper: 'white' },
    DISCOVER_STUDIO: { texture: 'nano_optical', font: 'arial', paper: 'white' },
    // ---- Regional banks ----------------------------------------------
    // REGIONAL wave-1 rebuilds (2026-11-05): the evidenced catalogue
    // ground for all six is the safety treatment ('security', the
    // studio's woven safety screen — HC's ground is literally named
    // Security Weave) on white safety paper; the decorative textures
    // the old builds carried live on the _STUDIO variants.
    // REGIONAL wave-2 rebuilds (2026-11-05): the same evidenced
    // catalogue ground for CITIZENS / ZIONS / COMERICA /
    // FIRST_HORIZON / M_AND_T; decorative textures live on their
    // _STUDIO variants. FIRST_HORIZON's design font becomes the
    // standard sans of its published identity (b).
    REGIONS: { texture: 'security', font: 'arial', paper: 'white' },
    TRUIST: { texture: 'security', font: 'helvetica', paper: 'white' },
    BMO: { texture: 'security', font: 'arial', paper: 'white' },
    ZIONS: { texture: 'security', font: 'helvetica', paper: 'white' },
    COMERICA: { texture: 'security', font: 'arial', paper: 'white' },
    FIRST_HORIZON: { texture: 'security', font: 'arial', paper: 'white' },
    M_AND_T: { texture: 'security', font: 'times', paper: 'white' },
    // ---- Credit unions -------------------------------------------------
    // — The house ground (a fabricated fibre/texture
    //   spectacle for each) gives way to the printers' measured woven
    //   safety treatment on white safety paper (the REGIONS rebuild
    //   move); each union's published colourway paints the design.
    //   PENFED's cream and GOLDEN1's ivory were not named safety
    //   designs → white (CITIZENS precedent). CREDIT UNION WAVE
    //   rebuild 2026-11-05; pre-rebuild grounds preserved on the
    //   _STUDIO variants (GOLDEN1_STUDIO keeps GOLDEN1's navy
    //   secondary verbatim; the main's secondary is the measured
    //   black of its published lockup).
    NAVY_FCU: { texture: 'security', font: 'arial', paper: 'white' },
    NAVY_FCU_STUDIO: { texture: 'fluorescent_fibers', font: 'arial', paper: 'white' },
    PENFED: { texture: 'security', font: 'arial', paper: 'white' },
    PENFED_STUDIO: { texture: 'linen', font: 'arial', paper: 'cream' },
    BECU: { texture: 'security', font: 'arial', paper: 'white' },
    BECU_STUDIO: { texture: 'digital_hive', font: 'arial', paper: 'white' },
    ALLIANT: { texture: 'security', font: 'arial', paper: 'white' },
    ALLIANT_STUDIO: { texture: 'particles', font: 'arial', paper: 'white' },
    GOLDEN1: { texture: 'security', font: 'arial', paper: 'white' },
    GOLDEN1_STUDIO: { texture: 'marble', font: 'arial', paper: 'ivory' },
    // ---- Specialty -----------------------------------------------------
    // SPECIALTY wave (2026-11-05): every specialty ground below
    // corrects to the EVIDENCED safety weave ('security') — the
    // decorative grounds (guilloche_bg, marble, rough, linen,
    // parchment, plain, toner_adhesion) were the pre-wave builds and
    // live verbatim on the _STUDIO variants. Exceptions, each on its
    // evidence: CASHIER keeps light green paper (Deluxe-printed
    // cashier's stock is light green (a)); TRAVELERS_CHECK takes the
    // engraved guilloche ground (the traveler's cheque's face
    // character (b)) on antique paper; COUNTER_CHECK is UNCHANGED
    // (researched — the plainest stock, the V4 reduced panel).
    CASHIER: { texture: 'security', font: 'georgia', paper: 'light_green' },
    CASHIER_STUDIO: { texture: 'guilloche_bg', font: 'georgia', paper: 'light_green' },
    PAYROLL: { texture: 'security', font: 'courier', paper: 'white' },
    PAYROLL_STUDIO: { texture: 'plain', font: 'courier', paper: 'white' },
    // DIGITAL wave (2026-11-05): an eCheck prints on NORMAL paper
    // (Deluxe Payment Exchange: "prints the check using normal paper
    // in either color or black and white" (a)) — so its ground is
    // PLAIN stock, not a woven safety ground: a safety weave would
    // falsely imply safety paper and contradict its own back ("NO
    // SECURITY PAPER FEATURES APPLY"). The pre-rebuild digital_hive
    // ground lives on ECHECK_STUDIO.
    ECHECK: { texture: 'plain', font: 'arial', paper: 'white' },
    ECHECK_STUDIO: { texture: 'digital_hive', font: 'arial', paper: 'white' },
    MONEY_ORDER: { texture: 'security', font: 'helvetica', paper: 'ivory' },
    MONEY_ORDER_STUDIO: { texture: 'parchment', font: 'helvetica', paper: 'ivory' },
    TRAVELERS_CHECK: { texture: 'guilloche_bg', font: 'times', paper: 'antique' },
    TRAVELERS_CHECK_STUDIO: { texture: 'parchment', font: 'times', paper: 'antique' },
    COUNTER_CHECK: { texture: 'plain', font: 'arial', paper: 'white' },
    CERTIFIED: { texture: 'security', font: 'times', paper: 'white' },
    CERTIFIED_STUDIO: { texture: 'marble', font: 'times', paper: 'antique' },
    CONTRACTOR: { texture: 'security', font: 'arial', paper: 'white' },
    CONTRACTOR_STUDIO: { texture: 'rough', font: 'arial', paper: 'white' },
    VOUCHER: { texture: 'security', font: 'courier', paper: 'white' },
    VOUCHER_STUDIO: { texture: 'linen', font: 'courier', paper: 'white' },
    INDUSTRIAL_PAYROLL: { texture: 'security', font: 'helvetica', paper: 'white' },
    INDUSTRIAL_PAYROLL_STUDIO: { texture: 'toner_adhesion', font: 'helvetica', paper: 'white' },
    // OWNER RULING rebuild (2026-11-05): AMEX_CENTURION's main
    // entry is the plain original Amex check (see bankTemplates) —
    // Harland Clarke is Amex's checkbook vendor (a), so the ground
    // is the evidenced safety treatment ('security', HC's Security
    // Weave) on white safety paper, replacing the guilloche ground
    // the Centurion concept carried. The pre-rebuild build is
    // preserved verbatim as AMEX_CENTURION_STUDIO.
    AMEX_CENTURION: { texture: 'security', font: 'arial', paper: 'white' },
    AMEX_CENTURION_STUDIO: { texture: 'guilloche_bg', font: 'georgia', paper: 'white' },
    // ---- Classic / generic ---------------------------------------------
    // FINAL wave rebuilds (2026-11-05): the catalogue classics take
    // the evidenced safety ground. CLASSIC_BLUE / CLASSIC_GREEN are
    // the Harland Clarke Guardian Safety designs (a) — the safety
    // weave on their tinted safety papers, the tint being the design
    // (the weave keeps each palette's primary: blue on blue, green
    // on green). CLASSIC_MAROON (Sheffield Maroon (a) / herringbone
    // maroon (b)) takes the same weave on its pale maroon paper; the
    // catalogue scene artwork is not expressible in tokens. The
    // pre-rebuild decorative grounds live on the _STUDIO variants.
    // GENERIC is RESEARCHED, UNCHANGED: its guilloche ground IS its
    // named premium-classic character and its set is already the
    // honest standard core plus its own drawn rosette. PARCHMENT and
    // MARBLE keep their named paper characters (the ground IS the
    // design); only their spec lists were corrected.
    GENERIC: { texture: 'guilloche_bg', font: 'arial', paper: 'white' },
    CLASSIC_BLUE: { texture: 'security', font: 'times', paper: 'light_blue' },
    CLASSIC_BLUE_STUDIO: { texture: 'linen', font: 'times', paper: 'light_blue' },
    CLASSIC_GREEN: { texture: 'security', font: 'times', paper: 'light_green' },
    CLASSIC_GREEN_STUDIO: { texture: 'linen', font: 'times', paper: 'light_green' },
    CLASSIC_MAROON: { texture: 'security', font: 'georgia', paper: 'light_pink' },
    CLASSIC_MAROON_STUDIO: { texture: 'guilloche_bg', font: 'georgia', paper: 'light_pink' },
    PARCHMENT: { texture: 'parchment', font: 'garamond', paper: 'antique' },
    MARBLE: { texture: 'marble', font: 'georgia', paper: 'white' },
    // ---- International ---------------------------------------------------
    // FINAL wave rebuilds (2026-11-05): CANADIAN takes the safety
    // weave on white — Standard 006 practice is light pastel safety
    // screening (b), expressed by the neutral slate weave (the
    // TEMPLATE_WEAVE_INK override below: the red primary at weave
    // alpha would paint the whole face red). The pre-rebuild linen
    // ground lives on CANADIAN_STUDIO. UK_STYLE stands: the engraved
    // guilloche character is the UK classic's evidenced face (b —
    // C&CCC security designs are fine-line guilloche work).
    CANADIAN: { texture: 'security', font: 'arial', paper: 'white' },
    CANADIAN_STUDIO: { texture: 'linen', font: 'arial', paper: 'white' },
    UK_STYLE: { texture: 'guilloche_bg', font: 'times', paper: 'ivory' },
    // ---- Owner specialty -------------------------------------------------
    KAREN_ART_STUDIO_PAYROLL: { texture: 'linen', font: 'arial', paper: 'white', opacity: 0.4 },
    // ---- Premium (non-bank) designs --------------------------------------
    classic_elegant: { texture: 'guilloche_bg', font: 'garamond', paper: 'antique', opacity: 0.9 },
    executive_gold: { texture: 'guilloche_bg', font: 'georgia', paper: 'antique', opacity: 0.9 },
    royal_blue: { texture: 'security', font: 'helvetica', paper: 'light_blue', opacity: 0.8 },
    corporate_grey: { texture: 'cotton', font: 'arial', paper: 'white' },
    emerald_prestige: { texture: 'marble', font: 'georgia', paper: 'light_green', opacity: 0.8 },
    burgundy_classic: { texture: 'royal_damask', font: 'garamond', paper: 'peach' },
    midnight_premium: { texture: 'interference', font: 'helvetica', paper: 'lavender' },
    bronze_heritage: { texture: 'parchment', font: 'georgia', paper: 'antique' },
    ultra_obsidian: { texture: 'geometric_diamond', font: 'helvetica', paper: 'white', opacity: 0.85 },
    ultra_sapphire: { texture: 'geometric_diamond', font: 'helvetica', paper: 'light_blue', opacity: 0.85 },
    ultra_amethyst: { texture: 'geometric_diamond', font: 'garamond', paper: 'light_pink', opacity: 0.85 },
    ultra_crimson: { texture: 'geometric_diamond', font: 'arial', paper: 'white', opacity: 0.85 },
    ultra_diamond: { texture: 'geometric_diamond', font: 'courier', paper: 'white', opacity: 0.85 },
    corporate_safety: { texture: 'blue_wave', font: 'arial', paper: 'light_blue' },
    us_treasury: { texture: 'fiber_optic', font: 'courier', paper: 'treasury_green' },
    chime_pro: { texture: 'digital_hive', font: 'helvetica', paper: 'white' },
    credit_union_classic: { texture: 'linen', font: 'times', paper: 'white' },
    varo_style: { texture: 'circuit_board', font: 'helvetica', paper: 'lavender' },
    prepaid_gold: { texture: 'particles', font: 'arial', paper: 'ivory' }
};

export interface StylePreset {
    border: string;
    texture: string;
    font: string;
    paper: string;
}

/**
 * Effective design for the check-layout styles. Before this map, the
 * style buttons only set the style id and Personal / Business /
 * Counter / Money Order rendered the identical check (same layout,
 * same default BRAIDED border, same texture and font). Each style now
 * also applies its own border / texture / font / paper — all real
 * studio parameters. A border of 'NONE' is deliberate for echeck,
 * counter, bofa_classic and bofa_v2: those styles carry their own
 * canvas-drawn frame logic in the renderer (borderless eCheck,
 * modern minimal counter frame, BofA classic double rule, BofA v2
 * braided ribbon), which only runs when the overlay border is NONE.
 *
 * payroll_karen is intentionally absent: it is the owner's client
 * design with its own full-page layout, and its look is set by its
 * template branch, never overridden here. Its style button is not a
 * no-op, though — it routes through that template branch instead
 * (see STYLE_TEMPLATE_ROUTES below). The three specimen styles
 * (greenfi, anb, usaa) are absent for the same reason: they route
 * through their template branches, which set their own design
 * tokens from TEMPLATE_DESIGN.
 */
export const STYLE_PRESETS: Record<string, StylePreset> = {
    personal: { border: 'DOUBLE', texture: 'linen', font: 'times', paper: 'cream' },
    business: { border: 'COMMERCIAL', texture: 'security', font: 'helvetica', paper: 'white' },
    payroll: { border: 'SOLID', texture: 'cotton', font: 'arial', paper: 'white' },
    payroll_classic: { border: 'DOUBLE', texture: 'parchment', font: 'garamond', paper: 'antique' },
    cashier: { border: 'TREASURY', texture: 'guilloche_bg', font: 'georgia', paper: 'light_green' },
    echeck: { border: 'NONE', texture: 'digital_hive', font: 'arial', paper: 'white' },
    money_order: { border: 'ORNATE', texture: 'parchment', font: 'georgia', paper: 'ivory' },
    certified: { border: 'FILIGREE', texture: 'marble', font: 'times', paper: 'antique' },
    counter: { border: 'NONE', texture: 'plain', font: 'arial', paper: 'white' },
    bofa_classic: { border: 'NONE', texture: 'cloud_marble', font: 'courier', paper: 'light_blue' },
    bofa_v2: { border: 'NONE', texture: 'blue_wave', font: 'arial', paper: 'white' },
    us_treasury: { border: 'TREASURY', texture: 'fiber_optic', font: 'courier', paper: 'treasury_green' }
};

/**
 * Style buttons that ARE a template (2026-11-03 Karen-button fix).
 *
 * Every other layout style is a generic preset above: the button sets
 * the style id plus border / texture / font / paper and the renderer
 * keys its furniture off the style id alone. payroll_karen is
 * different — the design's identity (account holder "Karen Art
 * Studio", bank "My Bank", payee, derived fractional routing, design
 * parameters) lives ONLY in the KAREN_ART_STUDIO_PAYROLL branch of
 * applyBankTemplate. Setting the style id alone therefore left the
 * previously selected design's bank, colours and data sitting on the
 * Karen layout, and from any other design the button read as dead:
 * the picker produced the Karen check, the button did not.
 *
 * The three 2026-11-04 specimen designs (GreenFi, ANB Bank, USAA)
 * are the same class: their identity furniture (wordmark / bank
 * block / Photo Safe mark, per-design security set, seal colour,
 * header suppression, MICR-caption treatment) lives only in their
 * applyBankTemplate branches, keyed off selectedBank. Their layout
 * styles therefore route through the template branch too, exactly
 * like Karen's — a style button that set only the id would print a
 * generic personal face wearing the design's name.
 *
 * applyStylePreset routes these style ids through applyBankTemplate,
 * so the button produces exactly the state the template picker
 * produces (selectedBank + style + data). styleButtons.test.ts pins
 * that every style id has a non-no-op apply path — a preset here or a
 * route there, never neither, never both — and that each route
 * round-trips: resolveTemplateStyle(route) is the style id itself.
 */
export const STYLE_TEMPLATE_ROUTES: Record<string, string> = {
    payroll_karen: 'KAREN_ART_STUDIO_PAYROLL',
    greenfi: 'GREENFI',
    anb: 'ANB',
    usaa: 'USAA'
};

/**
 * The check-layout style each template brings with it (2026-11-03
 * front-fix). Why this map exists: applyBankTemplate used to set only
 * the template id + design parameters and never the layout style, so
 * the previously selected style's face furniture leaked into every
 * template applied after it — pick Money Order, then Chase, and the
 * "MONEY ORDER / PERSONAL MONEY ORDER" heading stayed on the Chase
 * face; pick BofA Classic, then a premium design, and the premium
 * check inherited the BofA MICR variant (see micrVariantFor). A
 * template must yield its own complete face, so applying one now sets
 * the layout style from this map: type templates get their own layout
 * (money order, eCheck, cashier's, treasury, the two BofA layouts,
 * Karen payroll), the three specimen bank designs get their own
 * layouts too (2026-11-04 layout registration — until then they rode
 * the personal layout, so the layout grid had no GreenFi / ANB /
 * USAA entry at all), bank-issued business/retail designs get the
 * business layout, and member/classic/personal designs get the
 * personal layout (the premium split mirrors backVariants' business/
 * mobile families). micrVariant.test.ts enforces coverage for every
 * template id.
 */
export const TEMPLATE_STYLE: Record<string, string> = {
    // ---- Type templates: their own layout ------------------------------
    BOFA_CLASSIC: 'bofa_classic',
    BOFA_CLASSIC_STUDIO: 'bofa_classic',
    BOFA_BUSINESS_V2: 'bofa_v2',
    US_TREASURY_OFFICIAL: 'us_treasury',
    us_treasury: 'us_treasury',
    KAREN_ART_STUDIO_PAYROLL: 'payroll_karen',
    MONEY_ORDER: 'money_order',
    ECHECK: 'echeck',
    ECHECK_STUDIO: 'echeck',
    CASHIER: 'cashier',
    CASHIER_STUDIO: 'cashier',
    CERTIFIED: 'certified',
    CERTIFIED_STUDIO: 'certified',
    COUNTER_CHECK: 'counter',
    PAYROLL: 'payroll',
    PAYROLL_STUDIO: 'payroll',
    INDUSTRIAL_PAYROLL: 'payroll',
    INDUSTRIAL_PAYROLL_STUDIO: 'payroll',
    MONEY_ORDER_STUDIO: 'money_order',
    TRAVELERS_CHECK_STUDIO: 'personal',
    // ---- Bank-issued business / retail-major designs -------------------
    BOFA: 'business',
    CHASE: 'business',
    CHASE_PRIVATE: 'business',
    WELLS: 'business',
    CITI: 'business',
    PNC: 'business',
    TD: 'business',
    CAPITAL_ONE: 'business',
    US_BANK: 'business',
    HUNTINGTON: 'business',
    FIFTH_THIRD: 'business',
    KEYBANK: 'business',
    CITIZENS: 'business',
    ALLY: 'business',
    ALLY_STUDIO: 'business',
    DISCOVER: 'business',
    DISCOVER_STUDIO: 'business',
    REGIONS: 'business',
    TRUIST: 'business',
    BMO: 'business',
    REGIONS_STUDIO: 'business',
    TRUIST_STUDIO: 'business',
    HUNTINGTON_STUDIO: 'business',
    FIFTH_THIRD_STUDIO: 'business',
    KEYBANK_STUDIO: 'business',
    BOFA_STUDIO: 'business',
    CHASE_PRIVATE_STUDIO: 'business',
    WELLS_STUDIO: 'business',
    CITI_STUDIO: 'business',
    PNC_STUDIO: 'business',
    CAPITAL_ONE_STUDIO: 'business',
    US_BANK_STUDIO: 'business',
    AMEX_CENTURION_STUDIO: 'business',
    CITIZENS_STUDIO: 'business',
    ZIONS_STUDIO: 'business',
    COMERICA_STUDIO: 'business',
    FIRST_HORIZON_STUDIO: 'business',
    M_AND_T_STUDIO: 'business',
    ZIONS: 'business',
    COMERICA: 'business',
    FIRST_HORIZON: 'business',
    M_AND_T: 'business',
    AMEX_CENTURION: 'business',
    CONTRACTOR: 'business',
    CONTRACTOR_STUDIO: 'business',
    VOUCHER: 'business',
    VOUCHER_STUDIO: 'business',
    // ---- Member / classic / personal designs ---------------------------
    USAA: 'usaa',
    GREENFI: 'greenfi',
    GREENFI_MINT: 'personal',
    ANB: 'anb',
    ANB_SKY: 'anb',
    NAVY_FCU: 'personal',
    NAVY_FCU_STUDIO: 'personal',
    PENFED: 'personal',
    PENFED_STUDIO: 'personal',
    BECU: 'personal',
    BECU_STUDIO: 'personal',
    ALLIANT: 'personal',
    ALLIANT_STUDIO: 'personal',
    GOLDEN1: 'personal',
    GOLDEN1_STUDIO: 'personal',
    GENERIC: 'personal',
    CLASSIC_BLUE: 'personal',
    CLASSIC_BLUE_STUDIO: 'personal',
    CLASSIC_GREEN: 'personal',
    CLASSIC_GREEN_STUDIO: 'personal',
    CLASSIC_MAROON: 'personal',
    CLASSIC_MAROON_STUDIO: 'personal',
    PARCHMENT: 'personal',
    MARBLE: 'personal',
    CANADIAN: 'personal',
    CANADIAN_STUDIO: 'personal',
    UK_STYLE: 'personal',
    TRAVELERS_CHECK: 'personal',
    // ---- Premium designs (split mirrors backVariants' families) --------
    classic_elegant: 'business',
    executive_gold: 'business',
    royal_blue: 'business',
    corporate_grey: 'business',
    emerald_prestige: 'business',
    burgundy_classic: 'business',
    midnight_premium: 'business',
    bronze_heritage: 'business',
    ultra_obsidian: 'business',
    ultra_sapphire: 'business',
    ultra_amethyst: 'business',
    ultra_crimson: 'business',
    ultra_diamond: 'business',
    corporate_safety: 'business',
    prepaid_gold: 'business',
    chime_pro: 'personal',
    credit_union_classic: 'personal',
    varo_style: 'personal'
};

/** The layout style a template application selects. Unknown future
 *  ids fall back to the business layout rather than inheriting
 *  whatever style happened to be active (the leak this map fixes). */
/**
 * Per-template border treatment (2026-11-04 specimen designs).
 * Most templates keep whatever border the style/user has chosen;
 * a design whose printed frame IS its identity carries its own:
 * GreenFi's FSC microprint frame and USAA's bold navy double rules
 * (top and bottom edges only). Both styles are drawn by
 * utils/canvasBorder in the renderer's texture pass, so they print
 * and export with the check.
 *
 * ANB's entry is the opposite case (EXACT rebuild, 2026-11-04):
 * the specimen's scenic art bleeds to all four edges — no border
 * rule prints anywhere on the original — so the exact build pins
 * NONE (the border picker's own no-border entry; the owner's
 * GreenFi ruling is the same instinct: "the main one doesn't have
 * that borderline"). The earlier DOUBLE pin recorded the old
 * build's code history, not the specimen; that treatment is
 * preserved under ANB_SKY below, style-only exactly as before.
 */
export const TEMPLATE_BORDER: Record<string, { style: string; color?: string }> = {
    // GreenFi EXACT rebuild: the frame colour is the PALE
    // microprint green of the specimen's writing band — saturated
    // green belongs to the wordmark only.
    GREENFI: { style: 'MICROPRINT_FRAME', color: '#8cae7f' },
    // The preserved first build keeps its strong frame colour.
    GREENFI_MINT: { style: 'MICROPRINT_FRAME', color: '#2f9e44' },
    USAA: { style: 'DOUBLE_RULE_BOLD', color: '#0a2d5c' },
    // ANB EXACT rebuild: no borderline on the original (see the
    // note above). The preserved first build keeps its DOUBLE.
    ANB: { style: 'NONE' },
    ANB_SKY: { style: 'DOUBLE' }
};

/**
 * Per-template WORDMARK ink (fidelity programme, 2026-11-05).
 *
 * The bank-name write-up is normally painted with the template's
 * primary colour. A few banks' published identities split the roles:
 * the primary is the MARK's colour and the wordmark itself prints in
 * a different ink. KEYBANK is the evidenced case (regional wave 1's
 * parent render check caught it): the identity is a BLACK wordmark
 * with a RED key — primary #CC0000 is the key/accent, and the name
 * must render in the near-black secondary #1a1a1a. Templates absent
 * from this map keep the primary-ink behaviour, undisturbed.
 *
 * DISCOVER (digital wave, 2026-11-05) is the second evidenced case:
 * since 2001 the published mark is BLACK letters with only the
 * central "O" carrying the orange sun disc (b) — so #FF6600 (the
 * published Discover Orange) is the MARK's ink and stays the
 * primary, while the name itself prints in the studio's standing
 * neutral near-black #231F20 (the exact letter shade is (c); the
 * value is the neutral convention, as Wells' #231F20). DISCOVER_
 * STUDIO is absent on purpose: the preserved build keeps its
 * orange name verbatim.
 *
 * PNC (owner-ruling follow-up, 2026-11-05) is the third case, and
 * the inverse split: the MAJOR wave corrected the name's ROLE to
 * the palette's dark slate #1D242D because PNC's exact blue was
 * then single-sourced. Under Princewill's ruling that the
 * remaining templates take their banks' ORIGINAL designs, a fresh
 * pass firmed the hex: the published letters are "Super Blue"
 * #002FCD, named identically by two published logo references
 * (b — companylogos states it for the typography, schemecolor
 * lists it with Pantone 2728 C), so the name takes the sourced
 * blue here while the slate primary stays the design's dark ink
 * for its other elements (the Safety Blue ground is painted in
 * fixed blues and does not read the primary; the orange #FF6600
 * stays the roundel/accent as secondary). PNC_STUDIO is absent on
 * purpose: the preserved pre-rebuild build keeps its orange name
 * verbatim.
 */
export const TEMPLATE_WORDMARK_INK: Record<string, string> = {
    KEYBANK: '#1a1a1a',
    DISCOVER: '#231F20',
    PNC: '#002FCD'
};

/** The ink a template's bank-name write-up prints in, or undefined
 *  when the template's primary colour is the wordmark ink. */
export function templateWordmarkInk(bankId: string | null | undefined): string | undefined {
    return bankId ? TEMPLATE_WORDMARK_INK[bankId] : undefined;
}

/**
 * Per-template WEAVE ink (fidelity programme, 2026-11-05).
 *
 * The 'security' woven screen is stroked in the template's primary
 * colour by default, so the primary doubles as the face's tint.
 * That is the studio's standard safety ground for most designs —
 * but CITIZENS' evidenced design is UNTINTED white safety paper:
 * regional wave 2 corrected its paper token light_green → white
 * (no bank-specific safety tint is evidenced), yet the parent's
 * live render check still found the face reading pale mint. The
 * paper token was never what painted the face — the weave ink was:
 * wave 2 had also moved Citizens' primary to the saturated brand
 * green #00965E, and the crosshatch strokes it at ~38% alpha over
 * the whole face. The brand green belongs on the wordmark (where
 * the render pins keep it); the weave takes a neutral grey, so the
 * face renders as the white safety paper the research concluded.
 * Templates absent from this map keep the primary-ink weave,
 * undisturbed — including CITIZENS_STUDIO, whose preserved build
 * keeps its original green-tinted ground verbatim.
 *
 * GOLDEN1 (credit-union wave, 2026-11-05) is the same discriminating
 * case in another hue: its published wordmark ink IS its gold
 * primary (#FFB81C), so the name renders gold — but no gold-tinted
 * safety paper is evidenced, and a gold weave would paint the whole
 * face yellow. The face takes the same neutral slate weave; the
 * gold stays on the wordmark and furniture. GOLDEN1_STUDIO is
 * absent on purpose: its preserved marble ground renders no
 * 'security' weave at all.
 *
 * DISCOVER (digital wave, 2026-11-05) is the discriminating case in
 * orange: the published mark's orange (#FF6600) is the letter "O"'s
 * ink, not a paper tint — no orange-tinted safety paper is
 * evidenced, and an orange weave at ~38% alpha would paint the
 * whole face orange. The face takes the same neutral slate weave;
 * the orange stays on the mark/accents, and the name prints in
 * near-black (TEMPLATE_WORDMARK_INK). DISCOVER_STUDIO is absent on
 * purpose: its preserved nano_optical ground renders no 'security'
 * weave at all.
 *
 * CANADIAN (FINAL wave, 2026-11-05) is the discriminating case in
 * national red: the format's evidenced paper is white safety stock
 * with light pastel screening (Payments Canada Standard 006
 * practice (b)) — the red (#C41E3A) is the name/national styling
 * ink, not a paper tint, and a red weave at ~38% alpha would paint
 * the whole face pink-red. The face takes the same neutral slate
 * weave; the red stays on the name and furniture. CANADIAN_STUDIO
 * is absent on purpose: its preserved linen ground renders no
 * 'security' weave at all.
 */
export const TEMPLATE_WEAVE_INK: Record<string, string> = {
    CITIZENS: '#5f6a72',
    GOLDEN1: '#5f6a72',
    DISCOVER: '#5f6a72',
    CANADIAN: '#5f6a72'
};

/** The ink a template's woven safety screen is stroked in, or
 *  undefined when the weave takes the template's primary colour. */
export function templateWeaveInk(bankId: string | null | undefined): string | undefined {
    return bankId ? TEMPLATE_WEAVE_INK[bankId] : undefined;
}

export function resolveTemplateStyle(bankId: string): string {
    return TEMPLATE_STYLE[bankId] || 'business';
}

export type MicrVariant = 'standard' | 'bofa';

/**
 * Which MICR composition a design renders. The BofA pattern (routing
 * bracketed by transit symbols, account followed by the on-us symbol,
 * check number bare, no field captions) belongs ONLY to the BofA
 * Classic design — the bofa_classic layout style or the BOFA_CLASSIC
 * template (BOFA_CLASSIC_STUDIO, the classic design's preserved
 * marble build, rides the same bofa_classic style). Everything else,
 * including the BofA Business v2 design,
 * renders the standard composition (on-us-led account, check number
 * closed by on-us, TRANSIT ROUTING NUMBER / ACCOUNT NUMBER captions).
 * Single source for the gate so the renderer and the regression test
 * can never disagree.
 */
export function micrVariantFor(checkStyle: string, selectedBank: string | null | undefined): MicrVariant {
    return (checkStyle === 'bofa_classic' || selectedBank === 'BOFA_CLASSIC') ? 'bofa' : 'standard';
}
